import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export default function RefundPolicy() {
  return (
    <>
      <Helmet>
        <title>Refund &amp; Cancellation Policy | SACVPN</title>
        <meta name="description" content="SACVPN's refund and cancellation policy: start with a 14-day free trial (no credit card), a 30-day money-back guarantee on your first subscription, and cancel anytime from your dashboard." />
        <meta name="keywords" content="SACVPN refund policy, VPN money-back guarantee, cancel VPN subscription, VPN free trial, VPN cancellation" />
        <link rel="canonical" href="https://www.sacvpn.com/refund" />
        <meta property="og:title" content="Refund &amp; Cancellation Policy | SACVPN" />
        <meta property="og:description" content="14-day free trial with no credit card, a 30-day money-back guarantee, and cancel anytime. Here is exactly how it works." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sacvpn.com/refund" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-12 px-6">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-10 text-gray-800">
          <h1 className="text-4xl font-bold mb-6">Refund &amp; Cancellation Policy</h1>
          <p className="text-sm text-gray-600 mb-8">Last Updated: July 2026</p>

          <div className="max-w-none space-y-8">
            <section>
              <p className="mb-4">
                We want you to feel confident before and after you pay. This policy explains our free
                trial, our money-back guarantee, and how to cancel. It applies alongside our{' '}
                <Link to="/terms" className="text-brand-600 hover:underline font-semibold">Terms of Service</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">1. 14-Day Free Trial (No Credit Card)</h2>
              <p className="mb-4">
                New users can start a 14-day free trial with no credit card required. The trial lets you
                set up devices and use the Service before you decide to pay. If you do nothing at the end
                of the trial, it simply expires. You are never charged automatically from a trial, because
                we do not collect a payment method to start one.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. 30-Day Money-Back Guarantee</h2>
              <p className="mb-4">
                If you subscribe to a paid plan and decide it is not for you, first-time subscribers can
                request a full refund within 30 days of the initial purchase. To request one, email{' '}
                <a href="mailto:support@sacvpn.com" className="text-brand-600 hover:underline font-semibold">support@sacvpn.com</a>{' '}
                from your account email with the word "Refund" in the subject line. We process approved
                refunds to your original payment method, typically within 5 to 10 business days depending
                on your bank or card issuer.
              </p>
              <p className="mb-4 text-sm text-gray-600">
                The 30-day money-back guarantee applies to your first paid subscription. Renewals and
                repeat purchases are covered by the standard terms in Section 4 below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. How to Cancel</h2>
              <p className="mb-4">
                You can cancel your subscription at any time from your account dashboard under Billing.
                Cancellation stops the next renewal and your access continues until the end of the billing
                period you already paid for. There are no cancellation fees.
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Sign in and open your Billing settings</li>
                <li>Choose Cancel subscription and confirm</li>
                <li>You keep access until the current period ends</li>
              </ul>
              <p className="mb-4 text-sm text-gray-600">
                If you need help canceling, email{' '}
                <a href="mailto:support@sacvpn.com" className="text-brand-600 hover:underline font-semibold">support@sacvpn.com</a>{' '}
                and we will take care of it for you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. After the 30-Day Window</h2>
              <p className="mb-4">
                After the first 30 days, subscriptions are non-refundable except where required by law or
                at our discretion in exceptional circumstances (for example, a documented service outage
                that prevented you from using the Service). We do not provide prorated refunds for the
                unused part of a billing period after you cancel.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Situations That May Not Qualify</h2>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Accounts terminated for violating our Acceptable Use Policy</li>
                <li>Chargebacks filed without first contacting us to resolve the issue</li>
                <li>Refund requests made more than 30 days after the initial purchase (outside the guarantee)</li>
              </ul>
              <p className="mb-4 text-sm text-gray-600">
                We would always rather fix a problem than lose you over one. Reach out before disputing a
                charge and we will do our best to make it right.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Contact Us</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p><strong>SACVPN</strong>, a product of StephensCode LLC</p>
                <p>1135 Grand Central Parkway</p>
                <p>Conroe, TX 77304, United States</p>
                <p className="mt-2"><strong>Support:</strong> support@sacvpn.com</p>
                <p><strong>Website:</strong> https://www.sacvpn.com</p>
              </div>
            </section>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/download"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
          >
            Start your 14-day free trial
          </Link>
          <p className="text-gray-500 text-sm mt-3">No credit card required to start.</p>
        </div>
      </div>
    </>
  );
}
